import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Grid,
  Typography,
  Container,
  Card,
  Box,
  Link,
  Stack,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  Button,
  Divider
} from '@mui/material';

// third party
import { Formik } from 'formik';
import * as Yup from 'yup';

// project import
import AnimateButton from 'components/@extended/AnimateButton';
import { Http } from 'api/http/http';
import { logout } from 'store/reducers/auth';

const StudentInquiry = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleRefreshExpired = () => navigate('/studentLogin');
  const handleLogout = () => dispatch(logout());
  const http = new Http({ onRefreshExpired: handleRefreshExpired, onLogout: handleLogout });

  const copyEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      alert('메일주소가 클립보드에 복사되었습니다.');
    } catch (e) {
      alert('복사에 실패했습니다.');
    }
  };

  const InquirySchema = Yup.object().shape({
    department: Yup.string().max(50, '소속학과는 최대 50자까지 입력할 수 있습니다.').required('소속학과를 입력하세요.'),
    name: Yup.string().max(20, '이름은 최대 20자까지 입력할 수 있습니다.').required('이름을 입력하세요.'),
    studentId: Yup.string()
      .matches(/^[0-9]+$/, '학번은 숫자만 입력할 수 있습니다.')
      .max(20, '학번은 최대 20자까지 입력할 수 있습니다.')
      .required('학번을 입력하세요.'),
    email: Yup.string().email('올바른 이메일 형식이 아닙니다.').max(100, '이메일은 최대 100자까지 입력할 수 있습니다.').required('회신받을 이메일을 입력하세요.'),
    content: Yup.string().max(1500, '문의 내용은 최대 1500자까지 입력할 수 있습니다.').required('문의 내용을 입력하세요.')
  });

  const handleSubmit = async (values, { setErrors, setStatus, setSubmitting, resetForm }) => {
    try {
      setStatus({ success: false });
      setSubmitting(true);

      const response = await http.postWithToken('/api/students/inquiry', {
        department: values.department,
        name: values.name,
        studentNumber: values.studentId,
        email: values.email,
        content: values.content
      });

      if (response.status === 200) {
        setStatus({ success: true });
        alert('문의가 정상적으로 접수되었습니다. 입력하신 이메일로 회신드리겠습니다.');
        resetForm();
      } else {
        setErrors({ submit: '문의 전송에 실패했습니다. 잠시 후 다시 시도해주세요.' });
      }
    } catch (err) {
      setStatus({ success: false });
      setErrors({ submit: err.response?.data?.message || '문의 전송 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.' });
    }
    setSubmitting(false);
  };

  return (
    <Container>
      <Grid container spacing={4} justifyContent="center">
        <Grid item xs={12} md={8}>
          <Card sx={{ padding: '30px', marginTop: '20px' }}>
            <Typography variant="h4" gutterBottom>
              지능로봇 혁신 공유대학 사업단
            </Typography>
            <Typography variant="h5" gutterBottom>
              02-940-8431
            </Typography>
            <Typography variant="body1">
              마이크로모듈 관련 정책 담당자 이메일:
              <Link
                component="button"
                variant="body1"
                onClick={() => copyEmail('share@kw.ac.kr')}
                style={{ paddingLeft: 3, paddingBottom: 3, textTransform: 'none', textDecoration: 'underline' }}
              >
                share@kw.ac.kr
              </Link>
            </Typography>
            <Typography variant="body1">
              시스템 오류 담당자 이메일:
              <Link
                component="button"
                variant="body1"
                onClick={() => copyEmail('kw.share.service@gmail.com')}
                style={{ paddingLeft: 3, paddingBottom: 3, textTransform: 'none', textDecoration: 'underline' }}
              >
                kw.share.service@gmail.com
              </Link>
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ padding: '30px' }}>
            <Typography variant="h4" gutterBottom>
              문의하기
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              아래 내용을 작성해 문의를 보내주시면, 입력하신 이메일로 회신드립니다.
            </Typography>
            <Divider sx={{ marginY: '20px' }} />

            <Formik
              initialValues={{
                department: '',
                name: '',
                studentId: '',
                email: '',
                content: '',
                submit: null
              }}
              validationSchema={InquirySchema}
              onSubmit={handleSubmit}
            >
              {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, isValid, dirty }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="inquiry-department">소속학과</InputLabel>
                        <OutlinedInput
                          fullWidth
                          id="inquiry-department"
                          name="department"
                          value={values.department}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.department && errors.department)}
                          placeholder="예) 컴퓨터정보공학부"
                        />
                        {touched.department && errors.department && <FormHelperText error>{errors.department}</FormHelperText>}
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="inquiry-name">이름</InputLabel>
                        <OutlinedInput
                          fullWidth
                          id="inquiry-name"
                          name="name"
                          value={values.name}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.name && errors.name)}
                          placeholder="예) 홍길동"
                        />
                        {touched.name && errors.name && <FormHelperText error>{errors.name}</FormHelperText>}
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="inquiry-studentId">학번</InputLabel>
                        <OutlinedInput
                          fullWidth
                          id="inquiry-studentId"
                          name="studentId"
                          value={values.studentId}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.studentId && errors.studentId)}
                          placeholder="예) 2020123456"
                        />
                        {touched.studentId && errors.studentId && <FormHelperText error>{errors.studentId}</FormHelperText>}
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="inquiry-email">회신받을 이메일</InputLabel>
                        <OutlinedInput
                          fullWidth
                          id="inquiry-email"
                          name="email"
                          type="email"
                          value={values.email}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.email && errors.email)}
                          placeholder="회신받을 이메일 주소를 입력하세요"
                        />
                        {touched.email && errors.email && <FormHelperText error>{errors.email}</FormHelperText>}
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="inquiry-content">문의 내용</InputLabel>
                        <OutlinedInput
                          fullWidth
                          multiline
                          minRows={5}
                          id="inquiry-content"
                          name="content"
                          value={values.content}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.content && errors.content)}
                          placeholder="문의하실 내용을 자세히 작성해주세요."
                        />
                        {touched.content && errors.content && <FormHelperText error>{errors.content}</FormHelperText>}
                      </Stack>
                    </Grid>
                    {errors.submit && (
                      <Grid item xs={12}>
                        <FormHelperText error>{errors.submit}</FormHelperText>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <AnimateButton>
                        <Button
                          disableElevation
                          disabled={isSubmitting || !isValid || !dirty}
                          fullWidth
                          size="large"
                          type="submit"
                          variant="contained"
                          color="primary"
                        >
                          문의 보내기
                        </Button>
                      </AnimateButton>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          </Card>
        </Grid>
      </Grid>
      <Box sx={{ height: '20px' }} />
    </Container>
  );
};

export default StudentInquiry;
